import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";

/**
 * JournalContext — note giornaliere del sonno.
 *
 * Strategia online-first con cache localStorage:
 *  - Quando l'utente è loggato, le entries vivono in MongoDB
 *    (collection `user_journal_entries`) via /api/user/me/journal.
 *  - localStorage tiene una cache per startup veloce e accesso offline;
 *    le scritture vengono sincronizzate sul server.
 *  - Quando l'utente è anonimo (non ancora loggato) tutto sta in
 *    localStorage e verrà migrato al primo login (TODO se serve).
 */

const JournalContext = createContext(null);

const todayKey = () => {
  const d = new Date();
  return d.toISOString().slice(0, 10);
};

const cacheKey = (userId) =>
  userId ? `smartdream_journal_${userId}` : "smartdream_journal";

function readCache(userId) {
  try {
    const raw = localStorage.getItem(cacheKey(userId));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeCache(userId, entries) {
  try {
    localStorage.setItem(cacheKey(userId), JSON.stringify(entries));
  } catch {
    /* quota exceeded etc. */
  }
}

export function JournalProvider({ children }) {
  const { user, api } = useAuth();
  const [entries, setEntries] = useState({});
  const [ready, setReady] = useState(false);

  // On mount / user change: load cache + fetch dal backend
  useEffect(() => {
    let cancelled = false;
    setReady(false);
    setEntries(readCache(user?.id));

    if (!user?.id) {
      setReady(true);
      return;
    }

    (async () => {
      try {
        const r = await api.get("/api/user/me/journal?limit=200");
        if (cancelled) return;
        // Backend → dict per data
        const serverMap = {};
        for (const e of r.data.items || []) {
          serverMap[e.date] = e;
        }
        // MERGE strategy: LOCAL has priority. Le voci salvate sul device sono
        // sempre considerate "the source of truth" finché l'utente non le
        // sovrascrive esplicitamente, perché possono includere modifiche fatte
        // offline o casi in cui il server ha valori stale/scaduti.
        // Ordine: serverMap (base) ← localMap (override).
        const localMap = readCache(user.id);
        const merged = { ...serverMap, ...localMap };
        setEntries(merged);
        writeCache(user.id, merged);
      } catch {
        // Resta con la cache locale (per offline mode); NON azzeriamo nulla.
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, api]);

  const persistLocal = (next) => {
    setEntries(next);
    writeCache(user?.id, next);
  };

  const getEntry = (date = todayKey()) => entries[date] || null;

  const saveEntry = async (entry, date = todayKey()) => {
    // Optimistic update: aggiorna subito la UI + cache
    const local = {
      ...(entries[date] || {}),
      ...entry,
      date,
      updatedAt: Date.now(),
    };
    const next = { ...entries, [date]: local };
    persistLocal(next);

    // Sync server (best-effort): solo se loggato
    if (!user?.id) return local;
    try {
      const r = await api.post("/api/user/me/journal", { date, ...entry });
      // Sovrascrivi con il record canonico dal server
      const refreshed = { ...entries, [date]: r.data };
      persistLocal(refreshed);
      return r.data;
    } catch (e) {
      // Tieni la versione locale; verrà ritentata al prossimo write
      return local;
    }
  };

  const removeEntry = async (date) => {
    const next = { ...entries };
    delete next[date];
    persistLocal(next);
    if (!user?.id) return;
    try {
      await api.delete(`/api/user/me/journal/${date}`);
    } catch {
      /* silenzioso */
    }
  };

  const recentEntries = (limit = 7) =>
    Object.entries(entries)
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))
      .slice(0, limit)
      .map(([date, data]) => ({ date, ...data }));

  return (
    <JournalContext.Provider
      value={{
        entries,
        ready,
        todayKey,
        getEntry,
        saveEntry,
        removeEntry,
        recentEntries,
      }}
    >
      {children}
    </JournalContext.Provider>
  );
}

export function useJournal() {
  const ctx = useContext(JournalContext);
  if (!ctx) throw new Error("useJournal must be used within JournalProvider");
  return ctx;
}
